import React, { FC, Suspense, useEffect, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { backofficeUrl, T, toLocalID } from 'src/shared/helpers';
import { default as CogIcon, default as SettingsIcon } from 'src/shared/material_icons/action/settings.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import LinkOffIcon from 'src/shared/material_icons/content/link_off.svg';
import VerticalThreePoint from 'src/shared/material_icons/navigation/more_vert.svg';
import styled from 'styled-components';
import { Badge } from '../../widgets/badge';
import { BasicButton, GhostButton, Link, PrimaryButton, TextButton } from '../../widgets/buttons';
import { CollapsibleSection } from '../../widgets/collapsibleSection';
import { DialogWindow, MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { DropdownActionItem, DropdownActions } from '../../widgets/dropdown';
import { EmptyList } from '../../widgets/emptyList';
import { Icon } from '../../widgets/icon';
import { InlineHelpLayout } from '../../widgets/inlineHelpLayout';
import { ContainerBase } from '../../widgets/layouts';
import { removeNotification, showErrorNotification, showNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { PageHeader } from '../../widgets/pageHeader';
import { Section } from '../../widgets/section';
import { SectionContent } from '../../widgets/section/sectionContent';
import { Spinner } from '../../widgets/spinner';
import Stack from '../../widgets/stack/stack';
import { Row, Table, TableHeading } from '../../widgets/table';
import { ZTooltip } from '../../widgets/ztooltip';
import SalesChannelIcon from '../products/salesChannelIcon';
import { renameSalesChannelDialogMutation } from './__generated__/renameSalesChannelDialogMutation.graphql';
import { salesChannelProductsOnDefaultQuery } from './__generated__/salesChannelProductsOnDefaultQuery.graphql';
import { salesChannelRemoveIntegrationMutation } from './__generated__/salesChannelRemoveIntegrationMutation.graphql';
import { ECommerce, salesChannelsActive$data, salesChannelsActive$key } from './__generated__/salesChannelsActive.graphql';
import { salesChannelSetStatusMutation } from './__generated__/salesChannelSetStatusMutation.graphql';
import { salesChannelsQuery } from './__generated__/salesChannelsQuery.graphql';

import Loader from 'src/components/widgets/loader';
import { useAppState } from 'src/shared/appState';
import config from 'src/shared/config';
import { useFeatureFlag, useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { Center } from '../../../shared/globals';
import CreateStandaloneSiteDialog from '../settings/standaloneSites/createStandaloneSiteDialog';
import { getStandaloneSitePublicUrl } from '../settings/standaloneSites/standaloneSiteNavigation';
import { salesChannelDeleteMiniSiteMutation } from './__generated__/salesChannelDeleteMiniSiteMutation.graphql';
import { salesChannelMiniSitesQuery } from './__generated__/salesChannelMiniSitesQuery.graphql';
import { salesChannelRenameMiniSiteMutation } from './__generated__/salesChannelRenameMiniSiteMutation.graphql';
import AddSalesChannelDialog from './addSalesChannelDialog';
import ApiIntegrationSalesChannelDialog from './apiIntegrationSalesChannelDialog';
import ApiKeyDialog from './apiKeyDialog';
import CCloudIntegrationSalesChannelDialog from './cCloudIntegrationSalesChannelDialog';
import { generateStoreNumbers } from './helpers';
import InlineRenameCell from './inlineRenameCell';
import { updateNameSalesChannel } from './renameSalesChannelDialog';
import StoreUrlSalesChannelDialog from './storeUrlSalesChannelDialog';
import { TASKSTATUS, isTaskPending } from './switch/switchTypes';
import { useSalesChannelSwitch } from './switch/useSalesChannelSwitch';

const StyledImg = styled.div`
    > div {
        width: 32px;
        height: 32px;
    }

    img {
        width: 30px;
        height: 30px;
    }
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const StyledLinkContainer = styled.div`
    word-wrap: nowrap;
`;

const StyledDivElementTable = styled.div`
    align-content: center;
    max-width: 100%;
`;

const StyledLink = styled(Link)`
    font-weight: 400;

    span {
        word-break: break-all;
    }
`;

const StoreNumberCell = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SectionHeaderActionsGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const HeaderActionsDivider = styled.div`
    width: 1px;
    align-self: stretch;
    min-height: 24px;
    background: ${(props) => props.theme.app.borderColor};
`;

// Tighten the gap between the section header/description and the table below it.
const CompactSection = styled(Section)`
    ${SectionContent} {
        padding-top: 8px;
    }
`;

// Pull the empty state up so it stays visible even when the online stores table above is tall.
const CompactEmptyState = styled.div`
    > div {
        padding-top: 8px;
    }
`;

// Shared column template so the Online Stores and Mini-Sites tables line up column-for-column.
const SALES_CHANNEL_TABLE_WIDTHS = ['0.85fr', '1.21fr', 'minmax(auto, 500px)', '0.6fr', '1.14fr'];

const salesChannelMiniSitesQueryNode = graphql`
    query salesChannelMiniSitesQuery {
        standaloneSites {
            id
            standaloneSiteID
            name
            domain
            status
        }
    }
`;

const salesChannelRenameMiniSiteMutationNode = graphql`
    mutation salesChannelRenameMiniSiteMutation($input: StandaloneSiteUpdateInput!) {
        updateStandaloneSite(input: $input) {
            id
            name
        }
    }
`;

const salesChannelDeleteMiniSiteMutationNode = graphql`
    mutation salesChannelDeleteMiniSiteMutation($id: ID!) {
        deleteStandaloneSite(id: $id)
    }
`;

const renderMiniSiteStatusBadge = (status: string) => {
    switch (status) {
        case 'ACTIVE':
            return <Badge variant='success'>{T._('Active', 'Admin')}</Badge>;
        case 'DRAFT':
            return <Badge variant='warning'>{T._('Draft', 'Admin')}</Badge>;
        case 'DISABLED':
            return <Badge variant='danger'>{T._('Disabled', 'Admin')}</Badge>;
        default:
            return <Badge variant='neutral'>{status}</Badge>;
    }
};

const MiniSitesList: FC<{ fetchKey: number; onChanged: () => void; onAdd: () => void }> = ({ fetchKey, onChanged, onAdd }) => {
    const navigate = useNavigate();
    const { showDialog, closeDialog } = useDialogManager();
    const data = useLazyLoadQuery<salesChannelMiniSitesQuery>(salesChannelMiniSitesQueryNode, {}, { fetchKey, fetchPolicy: 'network-only' });
    const sites = data.standaloneSites || [];

    const [renameSite] = useMutation<salesChannelRenameMiniSiteMutation>(salesChannelRenameMiniSiteMutationNode);
    const [deleteSite] = useMutation<salesChannelDeleteMiniSiteMutation>(salesChannelDeleteMiniSiteMutationNode);

    const handleRename = (id: string, newName: string) => {
        renameSite({
            variables: { input: { id, name: newName } },
            onError: () => {
                showErrorNotification({ message: T._('Failed to rename mini-site.', 'Admin') });
            },
            onCompleted: () => {}
        });
    };

    const goToSettings = (standaloneSiteID: string) => navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(standaloneSiteID)}/settings`));
    const goToTheme = (standaloneSiteID: string) => navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(standaloneSiteID)}/themes`));

    const handleDelete = (site: { id: string; name: string }) => {
        showDialog(
            'delete-mini-site',
            <QuestionDialog
                showCloseButton={false}
                title={T._('Delete mini-site', 'Admin')}
                message={T._(`Are you sure you want to delete "${site.name}"? This action cannot be undone.`, 'Admin')}
                buttonYesLabel={T._('Cancel', 'Admin')}
                buttonNoLabel={T._('Delete', 'Admin')}
                destructiveNo
                onYesClick={() => closeDialog('delete-mini-site')}
                onNoClick={() => {
                    closeDialog('delete-mini-site');
                    deleteSite({
                        variables: { id: site.id },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Mini-site deleted.', 'Admin') });
                            onChanged();
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed to delete mini-site.', 'Admin') });
                        },
                        updater: (store) => store.invalidateStore()
                    });
                }}
            />
        );
    };

    if (sites.length === 0) {
        return (
            <CompactSection title={T._('Mini-Sites', 'Admin')}>
                <CompactEmptyState>
                    <EmptyList
                        hideIcon
                        title={''}
                        subTitle={T._(
                            'Create a branded mini-site powered by Zakeke, accessible directly on the web\nor embeddable in a physical kiosk, POS or similar solutions.',
                            'Admin'
                        )}
                        learnMoreText={T._('Learn more', 'Admin')}
                        learnMoreLink='https://zakeke.zendesk.com/hc/en-us'
                        buttonTitle={T._('Add', 'Admin')}
                        onButtonClick={onAdd}
                    />
                </CompactEmptyState>
            </CompactSection>
        );
    }

    const headings = [
        ['#', SALES_CHANNEL_TABLE_WIDTHS[0]],
        [T._('Name', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[1]],
        [T._('Url', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[2]],
        [T._('Status', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[3]],
        ['', SALES_CHANNEL_TABLE_WIDTHS[4]]
    ] as TableHeading[];

    return (
        <CompactSection
            title={T._('Mini-Sites', 'Admin')}
            description={T._(
                'Launch standalone customizer portals for B2B sales, kiosks, showrooms, POS, and other experiences that do not require Zakeke embedded in an online store.',
                'Admin'
            )}
            headerActions={
                <PrimaryButton $size='medium' onClick={onAdd} style={{ minWidth: '160px' }}>
                    <Icon>
                        <PlusIcon />
                    </Icon>
                    {T._('Add Mini-Site', 'Admin')}
                </PrimaryButton>
            }
        >
            <Table headings={headings} compact>
                {sites.map((site, i) => {
                    const publicUrl = getStandaloneSitePublicUrl(site.domain);
                    return (
                        <Row
                            key={site.id}
                            cells={[
                                <StyledDivElementTable>{T._(`Mini-Site ${i + 1}`, 'Admin')}</StyledDivElementTable>,
                                <InlineRenameCell name={site.name} onRename={(newName) => handleRename(site.id, newName)} />,
                                <StyledDivElementTable>
                                    {publicUrl ? (
                                        <StyledLinkContainer>
                                            <StyledLink href={publicUrl} target='_blank' title={publicUrl}>
                                                <span>{publicUrl}</span>
                                            </StyledLink>
                                        </StyledLinkContainer>
                                    ) : (
                                        '-'
                                    )}
                                </StyledDivElementTable>,
                                <StyledDivElementTable>{renderMiniSiteStatusBadge(site.status)}</StyledDivElementTable>,
                                <ActionsColContainer>
                                    <ZTooltip position={'top'} title={T._('Settings', 'Admin')}>
                                        <GhostButton
                                            $size='small'
                                            $type='icon'
                                            aria-label={T._('Settings', 'Admin')}
                                            onClick={() => goToSettings(site.standaloneSiteID.toString())}
                                        >
                                            <Icon>
                                                <SettingsIcon />
                                            </Icon>
                                        </GhostButton>
                                    </ZTooltip>
                                    <DropdownActions
                                        header={
                                            <Icon $hoverable>
                                                <VerticalThreePoint />
                                            </Icon>
                                        }
                                        orientation={'RIGHT'}
                                        sections={[
                                            [
                                                <DropdownActionItem
                                                    key='view'
                                                    disabled={!publicUrl}
                                                    onClick={() => publicUrl && window.open(publicUrl, '_blank')}
                                                >
                                                    {T._('View mini-site', 'Admin')}
                                                </DropdownActionItem>,
                                                <DropdownActionItem key='settings' onClick={() => goToSettings(site.standaloneSiteID.toString())}>
                                                    {T._('Settings', 'Admin')}
                                                </DropdownActionItem>,
                                                <DropdownActionItem key='theme' onClick={() => goToTheme(site.standaloneSiteID.toString())}>
                                                    {T._('Manage theme', 'Admin')}
                                                </DropdownActionItem>
                                            ],
                                            [
                                                <DropdownActionItem key='delete' danger onClick={() => handleDelete(site)}>
                                                    {T._('Delete', 'Admin')}
                                                </DropdownActionItem>
                                            ]
                                        ]}
                                    />
                                </ActionsColContainer>
                            ]}
                        />
                    );
                })}
            </Table>
        </CompactSection>
    );
};

const UnclickableDisconnectButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const SalesChannelNotAvailableDialogWindow = styled(DialogWindow)`
    p {
        font-size: 14px;
    }

    max-width: 600px;
`;

const StyledContainerBase = styled(ContainerBase)<{ showBackButton: boolean }>`
    padding-top: ${(props) => (props?.showBackButton ? '0' : '60px')};
`;

const HeroSection = styled.div`
    text-align: center;
    padding: 48px 24px 40px;
`;

const HeroTitle = styled.h2`
    font-size: 22px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    color: ${(props) => props.theme.app.textColor};
    margin: 0 0 12px;
`;

const HeroDescription = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.app.primary};
    margin: 0 auto;
    max-width: 520px;
    line-height: 1.6;
`;

const ChannelCardsGrid = styled.div<{ $singleColumn?: boolean }>`
    display: grid;
    grid-template-columns: ${(props) => (props.$singleColumn ? 'minmax(0, 450px)' : '1fr 1fr')};
    justify-content: center;
    gap: 24px;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const ChannelCard = styled.div`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    padding: 28px;
    position: relative;
    background: ${(props) => props.theme.app.backgroundColor};
    display: flex;
    flex-direction: column;
`;

const CardBadge = styled.span<{ $variant: 'store' | 'portal' }>`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${(props) => (props.$variant === 'store' ? 'rgba(232, 101, 10, 0.1)' : 'rgba(2, 136, 209, 0.1)')};
    color: ${(props) => (props.$variant === 'store' ? props.theme.app.secondary : props.theme.app.secondary_alt)};
    align-self: flex-start;
`;

const CardTitle = styled.h3`
    font-size: 18px;
    font-weight: ${(props) => props.theme.font.semi_bold};
    color: ${(props) => props.theme.app.textColor};
    margin: 16px 0 8px;
`;

const CardText = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.app.primary};
    margin: 0 0 20px;
    line-height: 1.5;
    /* Keep the description height consistent so both feature lists start at the same height */
    min-height: 84px;

    @media (max-width: 768px) {
        min-height: 0;
    }
`;

const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 24px;
`;

const FeatureItem = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${(props) => props.theme.app.textColor};
    padding: 4px 0;

    &::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${(props) => props.theme.app.component.notificationBanner.success.iconContainerBGColor};
        flex-shrink: 0;
    }
`;

const CardAction = styled.div`
    margin-top: auto;
`;

const FullWidthPrimaryButton = styled(PrimaryButton)`
    width: 100%;
`;

const InfoBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${(props) => props.theme.app.pageBackgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    padding: 16px 24px;
    /* Matches the cards row width above (grid max-width 900px minus its 24px side padding) */
    max-width: 852px;
    margin: 32px auto 0;
    gap: 16px;

    @media (max-width: 768px) {
        flex-direction: column;
        text-align: center;
    }
`;

const InfoBannerContent = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const InfoIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(2, 136, 209, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${(props) => props.theme.app.secondary_alt};
    font-size: 16px;
    font-weight: ${(props) => props.theme.font.semi_bold};
`;

const InfoBannerTextBlock = styled.div`
    font-size: 13px;
    color: ${(props) => props.theme.app.primary};
`;

export const activeSalesChannelFragment = graphql`
    fragment salesChannelsActive on Query @refetchable(queryName: "salesChannelActiveFragmentQuery") {
        user {
            userID
            email
        }
        salesChannels(status: ACTIVE) {
            id
            detail {
                id
                properties {
                    name
                }
            }
            ecommerceSettings {
                id
                ecommerceGlobalID
                ecommerceName
                remoteUrl
                isMarketplace
                definitiveUrl
                currencyCode
            }
        }
        productSwitcherTasks {
            taskID
            state
            taskType
            lastError
            elapsedTime
            modifiedOn
            extraData {
                taskID
                masterSalesChannelID
                secondarySalesChannelID
                delayMinutes
                paymentMethodSetupTaskID
            }
        }
        isSellerMigrating
    }
`;

const DisabledSalesChannelContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    cursor: not-allowed;
`;

const InfoText = styled.div`
    font-size: 12px;
    color: #78909c;
`;

const ProductItem = styled.div`
    padding: 4px 0;
    font-size: 13px;
    color: #374151;
`;

const RemainingProductsSection = styled.div`
    margin-top: 8px;

    > div {
        border-bottom: none;
    }
`;

const INLINE_PRODUCTS_LIMIT = 5;

const DefaultSalesChannelProductsList: FC = () => {
    const data = useLazyLoadQuery<salesChannelProductsOnDefaultQuery>(
        graphql`
            query salesChannelProductsOnDefaultQuery {
                productsOnDefaultSalesChannel {
                    id
                    code
                    name
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );

    const products = data.productsOnDefaultSalesChannel;
    if (products.length === 0) return null;

    const inlineProducts = products.slice(0, INLINE_PRODUCTS_LIMIT);
    const remainingProducts = products.slice(INLINE_PRODUCTS_LIMIT);
    const hasMore = remainingProducts.length > 0;

    return (
        <div>
            <ul>
                {inlineProducts.map((product) => (
                    <li key={product.id}>
                        {product.name} ({product.code})
                    </li>
                ))}
            </ul>
            {hasMore && (
                <RemainingProductsSection>
                    <CollapsibleSection title={T._('More affected products', 'Admin')} count={remainingProducts.length}>
                        {remainingProducts.map((product) => (
                            <ProductItem key={product.id}>
                                {product.name} ({product.code})
                            </ProductItem>
                        ))}
                    </CollapsibleSection>
                </RemainingProductsSection>
            )}
        </div>
    );
};

const HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels';

const SalesChannels: FC<{}> = () => {
    const navigate = useNavigate();
    const [fetchKey, setFetchKey] = useState(0);
    const [miniSitesFetchKey, setMiniSitesFetchKey] = useState(0);
    const { showDialog, closeDialog } = useDialogManager();
    const [isPending, startTransition] = useTransition();
    const { state } = useAppState();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const added = searchParams.get('added') || 'false';
    const isMaster = searchParams.get('isMaster') || 'false';

    const query = useLazyLoadQuery<salesChannelsQuery>(
        graphql`
            query salesChannelsQuery {
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                ...salesChannelsActive
                subscription {
                    areSalesChannelsEnabled
                    planId
                }
                standaloneSites {
                    standaloneSiteID
                }
            }
        `,
        {},
        { fetchKey, fetchPolicy: 'network-only' }
    );

    const [setSalesChannelStatus, isSetSalesChannelStatusPending] = useMutation<salesChannelSetStatusMutation>(graphql`
        mutation salesChannelSetStatusMutation($id: ID!, $status: SalesChannelStatus!) {
            setSalesChannelStatus(id: $id, status: $status) {
                id
            }
        }
    `);

    const [inlineRenameSalesChannel] = useMutation<renameSalesChannelDialogMutation>(updateNameSalesChannel);

    const [removeMasterIntegration] = useMutation<salesChannelRemoveIntegrationMutation>(graphql`
        mutation salesChannelRemoveIntegrationMutation($salesChannelID: ID) {
            removeEcommerceSettings(salesChannelID: $salesChannelID)
        }
    `);

    const [activeSalesChannels, refetch] = useRefetchableFragment<salesChannelsQuery, salesChannelsActive$key>(activeSalesChannelFragment, query);

    const isStandaloneEnabled = useFeatureFlag('standalone');

    const isSwitchSalesChannelAvailable = useFeatureFlagWithConditions('switchSalesChannel', {
        userId: activeSalesChannels.user.userID.toString()
    });

    const masterSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) == 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const otherSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) != 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const masterEcommerceSettings = masterSalesChannel[0]?.ecommerceSettings;

    const productSwitcherTasks = activeSalesChannels.productSwitcherTasks || [];
    const isSellerMigrating = activeSalesChannels.isSellerMigrating ?? false;

    const canShowSwitchButton =
        isSwitchSalesChannelAvailable &&
        (productSwitcherTasks.length === 0 ||
            (productSwitcherTasks.length > 0 &&
                productSwitcherTasks.every(
                    (task) => task.state !== TASKSTATUS.Waiting && task.state !== TASKSTATUS.Processing && task.state !== TASKSTATUS.Error
                )));

    const headings = [
        ['#', SALES_CHANNEL_TABLE_WIDTHS[0]],
        [T._('Name', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[1]],
        [T._('Url', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[2]],
        [T._('Platform', 'Admin'), SALES_CHANNEL_TABLE_WIDTHS[3]],
        ['', SALES_CHANNEL_TABLE_WIDTHS[4]]
    ] as TableHeading[];

    const buildRowCells = (salesChannel: salesChannelsActive$data['salesChannels'][number]) => {
        const name: string = salesChannel.detail?.properties?.name || '';

        const storeUrl = salesChannel.ecommerceSettings?.definitiveUrl || '';

        const storeNumber = storeNumbers.find((x) => x.salesChannelID === salesChannel.id)?.storeNumber || 1;

        // App-based master integrations can only be disconnected by removing the Zakeke app from the store.
        const cannotDisconnect =
            salesChannel.id === 'guid://Zakeke/SalesChannel/1' && ['SHOPIFY', 'BIG_COMMERCE', 'WIX'].includes(salesChannel.ecommerceSettings!.ecommerceName);

        return [
            <StyledDivElementTable>
                <StoreNumberCell>
                    {T._(`Store ${storeNumber}`, 'Admin')}
                    {toLocalID(salesChannel.id) == 1 && <Badge variant='info'>{T._('Master', 'Admin')}</Badge>}
                </StoreNumberCell>
            </StyledDivElementTable>,

            <InlineRenameCell name={name} onRename={(newName) => handleInlineRenameSalesChannel(salesChannel, newName)} />,
            <StyledDivElementTable>
                <StyledLinkContainer>
                    <StyledLink href={storeUrl} target='_blank' title={storeUrl}>
                        <span>{storeUrl}</span>
                    </StyledLink>
                </StyledLinkContainer>
            </StyledDivElementTable>,
            <StyledDivElementTable>
                <StyledImg>{SalesChannelIcon(salesChannel.ecommerceSettings?.ecommerceName, false)}</StyledImg>
            </StyledDivElementTable>,
            <ActionsColContainer>
                {!cannotDisconnect ? (
                    <ZTooltip position={'top'} title={T._('Disconnect', 'Admin')}>
                        <GhostButton
                            $size='small'
                            $type='icon'
                            aria-label={T._('Disconnect', 'Admin')}
                            onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}
                        >
                            <Icon>
                                <LinkOffIcon />
                            </Icon>
                        </GhostButton>
                    </ZTooltip>
                ) : (
                    <ZTooltip position={'top'} title={T._('To disconnect, remove the app from your store.', 'Admin')}>
                        <UnclickableDisconnectButtonContainer>
                            <GhostButton $size='small' $type='icon' disabled aria-label={T._('Disconnect', 'Admin')}>
                                <Icon>
                                    <LinkOffIcon />
                                </Icon>
                            </GhostButton>
                        </UnclickableDisconnectButtonContainer>
                    </ZTooltip>
                )}
                <DropdownActions
                    header={
                        <Icon $hoverable>
                            <VerticalThreePoint />
                        </Icon>
                    }
                    sections={[
                        [
                            storeUrl && (
                                <DropdownActionItem key={'view_store_button_' + salesChannel.id} onClick={() => window.open(storeUrl, '_blank')}>
                                    {T._('View store', 'Admin')}
                                </DropdownActionItem>
                            ),
                            <DropdownActionItem
                                key={'API_key_button_' + salesChannel.id}
                                onClick={() => {
                                    handleIntegrationSettingsAndApiKey(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                }}
                            >
                                {['API', 'C_CLOUD'].includes(salesChannel.ecommerceSettings!.ecommerceName)
                                    ? T._('Integration settings and API Keys', 'Admin')
                                    : T._('API Keys', 'Admin')}
                            </DropdownActionItem>,
                            ,
                            salesChannel.ecommerceSettings?.ecommerceName === 'PRESTA_SHOP' && (
                                <DropdownActionItem
                                    key={'edit_store_url_button_' + salesChannel.id}
                                    onClick={() => {
                                        handleChangeStoreUrlClick(salesChannel);
                                    }}
                                >
                                    {T._('Change store URL', 'Admin')}
                                </DropdownActionItem>
                            ),
                            salesChannel.ecommerceSettings?.ecommerceName &&
                                !['API', 'C_CLOUD', 'NONE'].includes(salesChannel.ecommerceSettings.ecommerceName) && (
                                    <DropdownActionItem
                                        key={'integration_settings_button_' + salesChannel.id}
                                        onClick={() => {
                                            const platform = salesChannel.ecommerceSettings!.ecommerceName;
                                            const url =
                                                platform === 'SHOPIFY'
                                                    ? backofficeUrl(`/settings/integration-settings/SHOPIFY?salesChannelID=${toLocalID(salesChannel.id)}`)
                                                    : backofficeUrl(`/settings/integration-settings/${platform}`);
                                            navigate(url);
                                        }}
                                    >
                                        {T._('Go to Integration Settings', 'Admin')}
                                    </DropdownActionItem>
                                ),
                            canShowSwitchButton &&
                                // Payment step disabled — a pending PAYMENT_CONFIRMED task no longer keeps this action visible.
                                // productSwitcherTasks.some(
                                //     (task) =>
                                //         task.taskType === 'PAYMENT_CONFIRMED' &&
                                //         isTaskPending(task.state) &&
                                //         task.extraData.secondarySalesChannelID?.toString() === toLocalID(salesChannel.id)?.toString()
                                // ) &&
                                salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                                salesChannel.id !== 'guid://Zakeke/SalesChannel/1' && (
                                    <DropdownActionItem
                                        key={'switch_sales_channel' + salesChannel.id}
                                        disabled={checkingSellerPlanForChannelID !== null}
                                        onClick={() => {
                                            handleSwitchSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                        }}
                                    >
                                        {checkingSellerPlanForChannelID === salesChannel.id ? <Spinner /> : T._('Set as Master Integration', 'Admin')}
                                    </DropdownActionItem>
                                )
                        ],
                        [
                            <DropdownActionItem
                                key={'disconnect_' + salesChannel.id}
                                danger
                                disabled={cannotDisconnect}
                                onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}
                            >
                                {T._('Disconnect', 'Admin')}
                            </DropdownActionItem>
                        ]
                    ]}
                    orientation={'RIGHT'}
                />
            </ActionsColContainer>
        ];
    };

    const salesChannelIDs = activeSalesChannels.salesChannels.filter((x) => x.ecommerceSettings?.ecommerceName !== 'NONE').map((channel) => channel.id || '');

    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    const canShowAddSalesChannelDialog = searchParams.get('showAddSalesChannelDialog') || 'false';

    const isFirstSalesChannel = !masterEcommerceSettings?.ecommerceGlobalID;

    const showBackButton = !(masterSalesChannel?.length > 0 && otherSalesChannel?.length == 0 && added === 'true');

    useEffect(() => {
        if (added === 'true' && !localStorage.getItem('pendingAction')) {
            showSuccessNotification({
                message: isMaster ? T._('Your Integration has been saved.', 'Admin') : T._('The updated list of sales channels will be shown soon', 'Admin'),
                dismiss: { duration: 5000 }
            });

            const timerId = setTimeout(() => {
                startTransition(() => setFetchKey((prev) => prev + 1));
                navigate(backofficeUrl('/sales-channels'), { replace: true });
            }, 5000);

            return () => clearTimeout(timerId);
        }
    }, [added, isMaster, location.key]);

    useEffect(() => {
        if (canShowAddSalesChannelDialog === 'true') {
            handleAddSaleChannel();
        }
    }, [canShowAddSalesChannelDialog]);

    useEffect(() => {
        // trigger every time the list of channels changes
        const pending = JSON.parse(localStorage.getItem('pendingAction') ?? 'null');
        if (!pending || pending.type !== 'publish-product') return;

        const integrationDone = activeSalesChannels.salesChannels.some((sc) => sc.ecommerceSettings?.ecommerceName !== 'NONE');
        if (!integrationDone) return;

        if (Date.now() - pending.ts > 10 * 60_000) {
            localStorage.removeItem('pendingAction');
            return;
        }

        localStorage.removeItem('pendingAction');

        navigate(backofficeUrl(pending.redirectPage), {
            replace: true,
            state: { doPublishImmediately: true, productID: pending.productId }
        });
    }, [activeSalesChannels.salesChannels]);

    // Declared here, not at the top of the component, so the switch flow's effects still run after the page's own.
    // Effect order decides dialog stacking: the reducer appends to an array that renders in order.
    const { checkingSellerPlanForChannelID, handleSwitchSalesChannel } = useSalesChannelSwitch({
        activeSalesChannels,
        productSwitcherTasks,
        isSellerMigrating,
        masterEcommerceSettings,
        planId: query.subscription?.planId,
        searchParams,
        setSearchParams,
        refetch
    });

    const handleAddSaleChannel = () => {
        if (isFirstSalesChannel) {
            navigate(backofficeUrl('/connect-shop'));
            return;
        }

        query.shopifyAuthEcommerceSettings?.isTestStore && config.isProduction
            ? showDialog(
                  'add-sales-channel-not-available',
                  <MessageDialog
                      windowDecorator={SalesChannelNotAvailableDialogWindow}
                      message={
                          <>
                              {T._(
                                  "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                  'Admin'
                              )}
                              <br />
                              <br />
                              {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                              <br />
                              <br />
                              {T._('Thank you for your understanding.', 'Admin')}
                          </>
                      }
                      buttons={[
                          {
                              label: T._('OK', 'Admin'),
                              onClick: () => {
                                  closeDialog('add-sales-channel-not-available');
                              }
                          }
                      ]}
                  />
              )
            : showDialog(
                  'add-sales-channel',
                  <AddSalesChannelDialog
                      onSalesChannelCreated={() => {
                          closeDialog('add-sales-channel');
                          setFetchKey((prev) => prev + 1);
                      }}
                  ></AddSalesChannelDialog>
              );
    };

    const handleCreateMiniSite = () => {
        const eligibleChannels = activeSalesChannels.salesChannels.filter(
            (sc) => sc.ecommerceSettings?.ecommerceName === 'NONE' || sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
        );

        showDialog(
            'create-standalone-site',
            <CreateStandaloneSiteDialog
                salesChannels={eligibleChannels}
                onCreated={(site) => {
                    closeDialog('create-standalone-site');
                    navigate(backofficeUrl(`/mini-sites/${encodeURIComponent(site.standaloneSiteID.toString())}/settings`));
                }}
            />
        );
    };

    const handleDeactivateSalesChannel = (id: string, ecommerce: ECommerce) => {
        const channel = activeSalesChannels.salesChannels.find((c) => c.id === id);
        const storeLabel = channel?.detail?.properties?.name || channel?.ecommerceSettings?.definitiveUrl || '';

        if (ecommerce === 'SHOPIFY' || ecommerce === 'BIG_COMMERCE') {
            showDialog(
                'disconnect-channel',
                <MessageDialog
                    message={T._('You must remove the Zakeke app from your store to disconnect this sales channel', 'Admin')}
                    onClose={() => {
                        closeDialog('disconnect-channel');
                    }}
                />
            );
        } else if (toLocalID(id) == 1) {
            showDialog(
                'disconnect-channel',
                <QuestionDialog
                    showCloseButton={false}
                    title={T._('Disconnect sales channel', 'Admin')}
                    message={
                        <>
                            {T._(
                                'Some of the products in your Customizable Products list originate from this sales channel. If you disconnect it, they will no longer be available for customization by your customers even on other sales channels.',
                                'Admin'
                            )}
                            {storeLabel && (
                                <>
                                    <br />
                                    <br />
                                    {T._('Sales channel:', 'Admin')} <strong>{storeLabel}</strong>
                                </>
                            )}
                            <br />
                            <br />
                            <Suspense fallback={<InfoText>{T._('Loading affected products...', 'Admin')}</InfoText>}>
                                <DefaultSalesChannelProductsList />
                            </Suspense>
                        </>
                    }
                    buttonYesLabel={T._('Cancel', 'Admin')}
                    buttonNoLabel={T._('Disconnect Anyway', 'Admin')}
                    destructiveNo
                    onYesClick={() => closeDialog('disconnect-channel')}
                    onNoClick={() => {
                        closeDialog('disconnect-channel');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._(`Disconnecting sales channel...`, 'Admin')
                        });

                        removeMasterIntegration({
                            variables: {
                                salesChannelID: null // Master integration
                            },
                            onError: () => {
                                removeNotification(loadingNotification);
                                showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                            },
                            onCompleted: () => {
                                removeNotification(loadingNotification);
                                showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                                setFetchKey((prev) => prev + 1);
                                if (added === 'true') {
                                    navigate(backofficeUrl('/sales-channels'));
                                }
                            }
                        });
                    }}
                />
            );
        } else {
            showDialog(
                'disconnect-channel',
                <QuestionDialog
                    showCloseButton={false}
                    title={T._('Disconnect sales channel', 'Admin')}
                    message={T._('Do you want to disconnect the sales channel?', 'Admin')}
                    buttonYesLabel={T._('Cancel', 'Admin')}
                    buttonNoLabel={T._('Disconnect', 'Admin')}
                    destructiveNo
                    onYesClick={() => closeDialog('disconnect-channel')}
                    onNoClick={() => {
                        closeDialog('disconnect-channel');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._(`Disconnecting sales channel...`, 'Admin')
                        });

                        setSalesChannelStatus({
                            variables: {
                                id: id,
                                status: 'DISABLED'
                            },
                            onError: () => {
                                removeNotification(loadingNotification);
                                showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                            },
                            onCompleted: () => {
                                removeNotification(loadingNotification);
                                showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                                refetch({}, { fetchPolicy: 'network-only' });
                            }
                        });
                    }}
                />
            );
        }
    };

    const handleInlineRenameSalesChannel = (salesChannel: salesChannelsActive$data['salesChannels'][number], newName: string) => {
        inlineRenameSalesChannel({
            variables: {
                input: {
                    id: salesChannel.detail?.id,
                    salesChannelGlobalID: salesChannel.id,
                    properties: { name: newName }
                }
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed rename sales channel', 'Admin') });
            },
            onCompleted: () => {
                refetch({}, { fetchPolicy: 'network-only' });
            }
        });
    };

    const handleIntegrationSettingsAndApiKey = (id: string, ecommerce: string) => {
        if (ecommerce === 'API') {
            showDialog('api-connection', <ApiIntegrationSalesChannelDialog salesChannelID={id || ''} hasMasterIntegration={true} />);
        } else if (ecommerce === 'C_CLOUD') {
            showDialog('sales-force', <CCloudIntegrationSalesChannelDialog salesChannelID={id || ''} />);
        } else {
            showDialog('api-key', <ApiKeyDialog salesChannelID={id} />);
        }
    };

    const handleChangeStoreUrlClick = (salesChannel: salesChannelsActive$data['salesChannels'][0]) => {
        showDialog('store-url-sales-channel', <StoreUrlSalesChannelDialog salesChannel={salesChannel} />);
    };

    const handleViewSwitchTasks = () => {
        navigate(backofficeUrl('/sales-channels/switch-tasks'));
    };

    const hasMasterEcommerce = !!(masterEcommerceSettings?.ecommerceName && masterEcommerceSettings.ecommerceName !== 'NONE');

    const miniSitesCount = isStandaloneEnabled ? (query.standaloneSites?.length ?? 0) : 0;

    // Show the section layout (with empty states) as soon as there is anything to manage.
    // Keep the two-card onboarding hero only when neither an online store nor a mini-site exists.
    const showSections = hasMasterEcommerce || miniSitesCount > 0;

    const pageHeaderDescription = isStandaloneEnabled
        ? T._('Connect your online stores and launch standalone mini-sites, then manage all your sales channels from a single place.', 'Admin')
        : T._('Connect your online stores and manage all your sales channels from a single place.', 'Admin');

    const heroDescription = isStandaloneEnabled
        ? T._(
              'Connect Zakeke to your online store or marketplace, or launch a standalone mini-site, and offer product customization wherever you sell.',
              'Admin'
          )
        : T._('Connect Zakeke to your online store or marketplace and offer product customization wherever you sell.', 'Admin');

    const allStores = [...masterSalesChannel, ...otherSalesChannel];

    return (
        <InlineHelpLayout helpUrl={HELP_URL}>
            {(openHelp) => (
                <StyledContainerBase showBackButton={showBackButton}>
                    {showSections && (
                        <PageHeader
                            title={T._('Sales Channels', 'Admin')}
                            pageKey='sales-channels'
                            backLabel={T._('Back', 'Admin')}
                            description={<>{pageHeaderDescription}</>}
                            learnMoreLabel={T._('Learn more', 'Admin')}
                            learnMoreUrl={HELP_URL}
                            onLearnMoreClick={openHelp}
                            isBackVisible={showBackButton}
                        />
                    )}

                    {showSections ? (
                        <>
                            {added !== 'false' && isPending && Loader}
                            {!isPending && (
                                <Stack>
                                    {hasMasterEcommerce ? (
                                        <CompactSection
                                            title={T._('Online Stores', 'Admin')}
                                            description={T._(
                                                'Online stores are e-commerce platforms connected to Zakeke. The store marked as Master controls billing and product sync for all channels.',
                                                'Admin'
                                            )}
                                            headerActions={
                                                <SectionHeaderActionsGroup>
                                                    {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && (
                                                        <ZTooltip position='bottom' title={T._('Integration Settings', 'Admin')}>
                                                            <GhostButton
                                                                $size='small'
                                                                $type='icon'
                                                                aria-label={T._('Integration Settings', 'Admin')}
                                                                onClick={() => navigate(backofficeUrl('/settings/integration-settings'))}
                                                            >
                                                                <Icon>
                                                                    <CogIcon />
                                                                </Icon>
                                                            </GhostButton>
                                                        </ZTooltip>
                                                    )}
                                                    {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && <HeaderActionsDivider />}
                                                    {productSwitcherTasks.length > 0 && (
                                                        <BasicButton onClick={() => handleViewSwitchTasks()}>
                                                            {T._('Sales Channels Tasks', 'Admin')}
                                                        </BasicButton>
                                                    )}
                                                    <PrimaryButton
                                                        $size='medium'
                                                        disabled={state.lastUsedTool === 2}
                                                        onClick={() => handleAddSaleChannel()}
                                                        style={{ minWidth: '160px' }}
                                                    >
                                                        <Icon>
                                                            <PlusIcon />
                                                        </Icon>
                                                        {T._('Add Online Store', 'Admin')}
                                                    </PrimaryButton>
                                                </SectionHeaderActionsGroup>
                                            }
                                        >
                                            {state.lastUsedTool === 2 && otherSalesChannel.length > 0 ? (
                                                <ZTooltip
                                                    position={'top'}
                                                    title={T._('Additional Sales Channels are not supported for 3D Configurator', 'Admin')}
                                                >
                                                    <DisabledSalesChannelContainer>
                                                        <Table headings={headings} compact>
                                                            {allStores.map((salesChannel, i) => (
                                                                <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                            ))}
                                                        </Table>
                                                    </DisabledSalesChannelContainer>
                                                </ZTooltip>
                                            ) : (
                                                <Table headings={headings} compact>
                                                    {allStores.map((salesChannel, i) => (
                                                        <Row key={i.toString()} cells={buildRowCells(salesChannel)} />
                                                    ))}
                                                </Table>
                                            )}
                                        </CompactSection>
                                    ) : (
                                        <CompactSection title={T._('Online Stores', 'Admin')}>
                                            <CompactEmptyState>
                                                <EmptyList
                                                    hideIcon
                                                    title={''}
                                                    subTitle={T._(
                                                        'Connect an online store or marketplace and embed Zakeke in your storefront as part of the buying experience.',
                                                        'Admin'
                                                    )}
                                                    buttonTitle={T._('Add Online Store', 'Admin')}
                                                    disabledButton={state.lastUsedTool === 2}
                                                    onButtonClick={() => handleAddSaleChannel()}
                                                />
                                            </CompactEmptyState>
                                        </CompactSection>
                                    )}

                                    {isStandaloneEnabled && (
                                        <Suspense
                                            fallback={
                                                <CompactSection title={T._('Mini-Sites', 'Admin')}>
                                                    <Center>
                                                        <Spinner />
                                                    </Center>
                                                </CompactSection>
                                            }
                                        >
                                            <MiniSitesList
                                                fetchKey={miniSitesFetchKey}
                                                onChanged={() => {
                                                    setMiniSitesFetchKey((prev) => prev + 1);
                                                    setFetchKey((prev) => prev + 1);
                                                }}
                                                onAdd={() => handleCreateMiniSite()}
                                            />
                                        </Suspense>
                                    )}
                                </Stack>
                            )}
                        </>
                    ) : (
                        <>
                            {added !== 'false' && Loader}
                            {added === 'false' && (
                                <>
                                    <HeroSection>
                                        <HeroTitle>{T._('Bring Zakeke to your customers', 'Admin')}</HeroTitle>
                                        <HeroDescription>{heroDescription}</HeroDescription>
                                    </HeroSection>

                                    <ChannelCardsGrid $singleColumn={!isStandaloneEnabled}>
                                        <ChannelCard>
                                            <CardBadge $variant='store'>{T._('Store', 'Admin')}</CardBadge>
                                            <CardTitle>{T._('Connect a Store', 'Admin')}</CardTitle>
                                            <CardText>
                                                {T._(
                                                    'Connect an online e-commerce or marketplace, B2C or B2B — Shopify, WooCommerce, Magento and more. Zakeke is embedded in your store as part of the buying experience.',
                                                    'Admin'
                                                )}
                                            </CardText>
                                            <FeatureList>
                                                <FeatureItem>{T._('Online store or marketplace, B2C or B2B', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Zakeke embedded in your storefront', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Part of the purchase experience', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Code-free connectors or API', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Automatic Product Sync', 'Admin')}</FeatureItem>
                                                <FeatureItem>{T._('Real-time Order Management', 'Admin')}</FeatureItem>
                                            </FeatureList>
                                            <CardAction>
                                                <FullWidthPrimaryButton $size='medium' onClick={() => handleAddSaleChannel()}>
                                                    {T._('Connect Store', 'Admin')}
                                                </FullWidthPrimaryButton>
                                            </CardAction>
                                        </ChannelCard>

                                        {isStandaloneEnabled && (
                                            <ChannelCard>
                                                <CardBadge $variant='portal'>{T._('Mini-Site', 'Admin')}</CardBadge>
                                                <CardTitle>{T._('Create a Mini-Site', 'Admin')}</CardTitle>
                                                <CardText>
                                                    {T._(
                                                        'Launch a standalone customizer portal hosted by us. Ideal for kiosks, showrooms, B2B sales, and proofing.',
                                                        'Admin'
                                                    )}
                                                </CardText>
                                                <FeatureList>
                                                    <FeatureItem>{T._('Ideal for kiosk & showroom experiences', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Great for B2B sales & proofing', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Customizable Theme', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Custom Sub-domain', 'Admin')}</FeatureItem>
                                                    <FeatureItem>{T._('Products List & Collections', 'Admin')}</FeatureItem>
                                                </FeatureList>
                                                <CardAction>
                                                    <FullWidthPrimaryButton $size='medium' onClick={() => handleCreateMiniSite()}>
                                                        {T._('Create Mini-Site', 'Admin')}
                                                    </FullWidthPrimaryButton>
                                                </CardAction>
                                            </ChannelCard>
                                        )}
                                    </ChannelCardsGrid>

                                    {isStandaloneEnabled && (
                                        <InfoBanner>
                                            <InfoBannerContent>
                                                <InfoIcon>i</InfoIcon>
                                                <InfoBannerTextBlock>
                                                    <strong>{T._('Need help deciding?', 'Admin')}</strong>
                                                    <div>
                                                        {T._('Compare the features of stores versus standalone mini-sites in our documentation.', 'Admin')}
                                                    </div>
                                                </InfoBannerTextBlock>
                                            </InfoBannerContent>
                                            <TextButton onClick={openHelp}>{T._('Read Guide', 'Admin')}</TextButton>
                                        </InfoBanner>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </StyledContainerBase>
            )}
        </InlineHelpLayout>
    );
};

export default SalesChannels;
